import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
    ALLOWED_REMOTE_VIEWER_ROLES,
    SCANNING_ROUTES_ALLOWED_ROLES,
    TABLE_DEFAULT_CURRENT_PAGE,
    TABLE_DEFAULT_PAGE_SIZE
} from '../AppConstants';
import ErrorHandler from '../components/ErrorHandler';
import { userHasRoleAccess } from '../helpers/AccessManagementHelpers';
import { formatObjectToQueryString } from '../helpers/FormatObjectToQueryString';
import { getPaginatorParamsFromURL } from '../helpers/URLHelpers';
import { getPaginatedScans } from '../services/Scans';
import { GetScansQueryParams, OverviewMode, ScanRes } from '../types/Scan';
import { PaginatedResponse } from '../types/Table';
import { User } from '../types/User';

/**
 * @deprecated SS-445 - We are migrating away from using this hook
 *
 * This hook provides data via API for the Scanning Overview component
 */
const useScanningOverviewProvider = (currentUser: User | undefined): any => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [mode, setMode] = useState<OverviewMode>(
        location.pathname.includes('arriving-loggers') ? 'ARRIVING_LOGGERS' : 'SCANNING'
    );

    const [currentPage, setCurrentPage] = useState<number | null>(null);
    const [pageSize, setPageSize] = useState<number | null>(null);
    const [filterCriteria, setFilterCriteria] = useState('');
    const [filterId, setFilterId] = useState('');
    const [hasAccess] = useState(() =>
        currentUser ? userHasRoleAccess(currentUser, SCANNING_ROUTES_ALLOWED_ROLES) : false
    );
    const [filterLocations, setFilterLocations] = useState<Array<string>>([]);

    const isUserRemoteViewer = useMemo(
        () => userHasRoleAccess(currentUser, ALLOWED_REMOTE_VIEWER_ROLES),
        [currentUser]
    );

    const scansQueryParams: GetScansQueryParams | null =
        currentPage && pageSize
            ? {
                  page: currentPage,
                  show: pageSize,
                  ...(filterId && { [filterCriteria]: filterId }),
                  ...(mode === 'ARRIVING_LOGGERS' && { arriving: true }),
                  ...(isUserRemoteViewer && filterLocations.length > 0 ? { userLocations: filterLocations } : {})
              }
            : null;

    // TODO: SS-445 - This is a temporary fix to avoid unnecessary API calls when the user is not on the scanning/arriving loggers page
    //  This provider hook will be removed in SS-445, and the logic will be handled in a different way.
    const inScanningOrArrivingLoggers = location.pathname === '/scanning' || location.pathname === '/arriving-loggers';
    const isScansQueryEnabled = Boolean(scansQueryParams && currentUser && inScanningOrArrivingLoggers);

    const paginatedResponse = useQuery<PaginatedResponse<ScanRes>, Error>({
        queryKey: ['scans', scansQueryParams],
        queryFn: () => getPaginatedScans(formatObjectToQueryString(scansQueryParams)),
        enabled: isScansQueryEnabled,
        placeholderData: keepPreviousData
    });

    useEffect(() => {
        const { pageIndex, pageSize } = getPaginatorParamsFromURL(searchParams);

        setCurrentPage(pageIndex ?? TABLE_DEFAULT_CURRENT_PAGE);
        setPageSize(pageSize ?? TABLE_DEFAULT_PAGE_SIZE);

        const userLocation = searchParams.get('userLocation');
        setFilterLocations(userLocation ? userLocation.split(',') : []);
    }, [searchParams]);

    useEffect(() => {
        if (mode && currentUser) {
            setFilterLocations([currentUser.locationName]);
            setCurrentPage(TABLE_DEFAULT_CURRENT_PAGE);
            setPageSize(TABLE_DEFAULT_PAGE_SIZE);
        }
    }, [mode]);

    useEffect(() => {
        if (filterLocations?.length === 0 && hasAccess) {
            // Clear the userLocation in the query if filterLocation is empty
            const currentUrl = `${location.pathname}${location.search || ''}`;
            const url = new URL(currentUrl, document.location.href);
            if (url.pathname === '/scanning') {
                url.searchParams.delete('userLocation');
                navigate(`${url.pathname}${url.search}`);
            }
        }
        if (mode && hasAccess && filterLocations) {
            const { _filterCriteria, _filterId } = getQueryParamsAndSetStateOnMount();

            if (_filterCriteria && _filterId) {
                setFilterCriteria(_filterCriteria);
                setFilterId(_filterId);
                filterScansHandler(_filterCriteria, _filterId);
            }
        }
    }, [hasAccess, filterLocations]);

    // This effect handles response from getPaginatedScans endpoint
    useEffect(() => {
        // TODO: SS-445 - Low hanging fix for a unwanted URL changes in Box Overview, this provider will be removed in
        //  in ticket SS-445, and will be fixed in a proper way.
        if (!isScansQueryEnabled) return;

        if (paginatedResponse.data) {
            const totalCount = paginatedResponse.data.count;
            const _pageSize = paginatedResponse.data.show;
            const currentUrl = `${location.pathname}${location.search || ''}`;
            const urlWithQueryParam = getUrlWithQueryParamsOnResponse(currentUrl, currentPage, _pageSize, totalCount);

            // don't use the url if that's the current address
            if (urlWithQueryParam !== currentUrl) {
                navigate(urlWithQueryParam, { replace: true });
            }
        } else if (paginatedResponse.error) {
            handlePageSizeChange(TABLE_DEFAULT_PAGE_SIZE);
            setCurrentPage(TABLE_DEFAULT_CURRENT_PAGE);
            ErrorHandler(paginatedResponse.error);
        }
    }, [paginatedResponse.data, paginatedResponse.error]);

    // This effect listens for changes in url and sets the correct overview mode
    useEffect(() => {
        const inView = location.pathname.includes('arriving-loggers') ? 'ARRIVING_LOGGERS' : 'SCANNING';
        if (inView !== mode) {
            setMode(inView);
            setFilterCriteria('');
            setFilterId('');
        }
    }, [location]);

    const filterScansHandler = (criteria: string, filterVal: string): void => {
        setFilterCriteria(criteria);
        setFilterId(filterVal);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (_pageSize: number): void => {
        setPageSize(_pageSize);
        setCurrentPage(1);
    };

    const getUrlWithQueryParamsOnResponse = (
        currentUrl: string,
        _currentPage: number | null,
        _pageSize: number,
        totalCount: number
    ): string => {
        const url = new URL(currentUrl, document.location.href);

        url.searchParams.delete('loggerId');
        url.searchParams.delete('trackingId');
        url.searchParams.delete('salesOrderId');
        url.searchParams.delete('destinationName');
        url.searchParams.delete('userLocation');

        const totalPages = Math.ceil(totalCount / _pageSize);

        if (_currentPage && _currentPage > 1 && _currentPage <= totalPages) {
            url.searchParams.set('page', _currentPage.toString());
        } else if (_currentPage && _currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
            url.searchParams.set('page', totalPages.toString());
        } else {
            url.searchParams.delete('page');
        }

        if (_pageSize > 10) {
            url.searchParams.set('pageSize', _pageSize.toString());
        } else {
            url.searchParams.delete('pageSize');
        }

        if (filterCriteria && filterId) {
            url.searchParams.set(filterCriteria.toString(), filterId);
        }

        if (isUserRemoteViewer && filterLocations?.length > 0) {
            url.searchParams.set('userLocation', filterLocations.join(','));
        }

        return `${url.pathname}${url.search}`;
    };

    const getQueryParamsAndSetStateOnMount = (): any => {
        const urlParams = new URLSearchParams(location.search);
        const loggerId = urlParams.get('loggerId');
        const trackingId = urlParams.get('trackingId');
        const salesOrderId = urlParams.get('salesOrderId');
        const destinationName = urlParams.get('destinationName');
        let _filterCriteria = '';
        let _filterId = '';
        if (loggerId) {
            _filterCriteria = 'loggerId';
            _filterId = loggerId;
        } else if (trackingId) {
            _filterCriteria = 'trackingId';
            _filterId = trackingId;
        } else if (salesOrderId) {
            _filterCriteria = 'salesOrderId';
            _filterId = salesOrderId;
        } else if (destinationName) {
            _filterCriteria = 'destinationName';
            _filterId = destinationName;
        }
        return { _filterCriteria, _filterId };
    };

    return {
        currentPage,
        pageSize,
        mode,
        currentUser,
        handlePageSizeChange,
        paginatedResponse: paginatedResponse,
        setCurrentPage,
        setPageSize,
        filterScansHandler,
        filterCriteria,
        filterId,
        setFilterLocations,
        filterLocation: filterLocations
    };
};

export default useScanningOverviewProvider;
